import { useEffect, useState } from "react";
import { isApiError, programApi } from "@bw/shared/api";
import {
  useProgramPreviewPage,
  useProgramPreviewQuery,
} from "@bw/shared/hooks";
import type { ProgramTrackOfferingLabelResponse } from "@bw/shared/types/generated";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ClassOfferingPickerProps } from "./schema";

const PAGE_SIZE = 25;

/** One sequential queue for the current visible price-rule targets. */
export function useVisibleClassOfferingLabels(
  facilityId: string,
  authorityGeneration: string,
  offeringIds: readonly string[],
  enabled: boolean,
) {
  const ids = [...new Set(offeringIds)];
  return useProgramPreviewQuery({
    scope: [
      authorityGeneration,
      "rates-offerings",
      facilityId,
      "visible-labels",
      ids,
    ],
    enabled: enabled && ids.length > 0,
    load: async (signal, isCurrent) => {
      if (ids.length > PAGE_SIZE)
        throw new Error("Too many visible class options.");
      const labels: Record<string, ProgramTrackOfferingLabelResponse | null> =
        {};
      for (const id of ids) {
        if (!isCurrent())
          throw new DOMException("Class labels were superseded", "AbortError");
        try {
          labels[id] = await programApi.getTrackOfferingLabel(
            id,
            facilityId,
            signal,
          );
        } catch (error) {
          if (!isApiError(error) || error.status !== 404) throw error;
          labels[id] = null;
        }
      }
      return labels;
    },
  });
}

export function ClassOfferingPicker({
  facilityId,
  authorityGeneration,
  offeringId,
  disabled,
  onChange,
}: ClassOfferingPickerProps) {
  const scopeToken = JSON.stringify([facilityId, authorityGeneration]);
  const [selection, setSelection] = useState({
    scopeToken,
    programId: "",
    programName: "",
    picked: false,
  });
  const current =
    selection.scopeToken === scopeToken
      ? selection
      : { scopeToken, programId: "", programName: "", picked: false };
  const label = useProgramPreviewQuery({
    scope: [
      authorityGeneration,
      "rates-offerings",
      facilityId,
      "selected-label",
      offeringId,
    ],
    enabled: Boolean(offeringId),
    load: (signal) =>
      programApi.getTrackOfferingLabel(offeringId, facilityId, signal),
  });
  const selectedProgramId = current.picked
    ? current.programId
    : (label.data?.program_id ?? "");
  const selectedProgramName = current.picked
    ? current.programName
    : (label.data?.program_name ?? "");
  const [navigation, setNavigation] = useState({
    scopeToken,
    offsets: [0],
    selected: 0,
  });
  const navigationCurrent =
    navigation.scopeToken === scopeToken
      ? navigation
      : { scopeToken, offsets: [0], selected: 0 };
  const offset = navigationCurrent.offsets[navigationCurrent.selected];
  const programs = useProgramPreviewQuery({
    scope: [
      authorityGeneration,
      "rates-offerings",
      facilityId,
      "programs",
      offset,
    ],
    enabled:
      !offeringId ||
      label.ready ||
      (isApiError(label.error) && label.error.status === 404),
    load: (signal) =>
      programApi.listPrograms(
        { facility_id: facilityId, limit: PAGE_SIZE, offset },
        signal,
      ),
  });
  const tracks = useProgramPreviewPage({
    scope: [
      authorityGeneration,
      "rates-offerings",
      facilityId,
      "options",
      selectedProgramId,
    ],
    enabled: Boolean(selectedProgramId),
    load: (cursor, signal) =>
      programApi.getTracks(
        selectedProgramId,
        { limit: PAGE_SIZE, active_only: false, ...(cursor ? { cursor } : {}) },
        signal,
      ),
  });
  const programItems = programs.data?.items ?? [];
  const trackItems = tracks.data?.items ?? [];
  const savedTrack =
    label.data?.program_id === selectedProgramId ? label.data : undefined;
  const canNextProgram = Boolean(
    programs.data && offset + programItems.length < programs.data.total,
  );
  useEffect(() => {
    if (
      current.picked &&
      label.data &&
      label.data.program_id !== current.programId
    ) {
      onChange("");
    }
  }, [
    scopeToken,
    offeringId,
    label.data,
    current.programId,
    current.picked,
    onChange,
  ]);

  return (
    <Stack spacing={1.5}>
      {label.isError ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={label.restart}>
              Retry class option
            </Button>
          }
        >
          {isApiError(label.error) && label.error.status === 404
            ? "Saved class option is unavailable."
            : "Class option unavailable right now."}
        </Alert>
      ) : null}
      {programs.isError ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={programs.restart}>
              Retry programs
            </Button>
          }
        >
          Programs unavailable right now.
        </Alert>
      ) : null}
      <FormControl
        size="small"
        fullWidth
        disabled={disabled || !programs.ready}
      >
        <InputLabel id="class-program-label">Program</InputLabel>
        <Select
          labelId="class-program-label"
          label="Program"
          value={selectedProgramId}
          onChange={(event) => {
            const item = programItems.find(
              (program) => program.program_id === event.target.value,
            );
            setSelection({
              scopeToken,
              picked: true,
              programId: event.target.value,
              programName: item?.name ?? "",
            });
            onChange("");
          }}
        >
          <MenuItem value="">Choose a program</MenuItem>
          {selectedProgramId &&
          !programItems.some(
            (program) => program.program_id === selectedProgramId,
          ) ? (
            <MenuItem value={selectedProgramId}>{selectedProgramName}</MenuItem>
          ) : null}
          {programItems.map((program) => (
            <MenuItem key={program.program_id} value={program.program_id}>
              {program.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button
          size="small"
          disabled={
            disabled || !programs.ready || navigationCurrent.selected === 0
          }
          onClick={() =>
            setNavigation({
              ...navigationCurrent,
              selected: navigationCurrent.selected - 1,
            })
          }
        >
          Previous programs
        </Button>
        <Button
          size="small"
          disabled={disabled || !programs.ready || !canNextProgram}
          onClick={() => {
            const nextOffset = offset + PAGE_SIZE;
            setNavigation(
              navigationCurrent.offsets[navigationCurrent.selected + 1] ===
                nextOffset
                ? {
                    ...navigationCurrent,
                    selected: navigationCurrent.selected + 1,
                  }
                : { scopeToken, offsets: [offset, nextOffset], selected: 1 },
            );
          }}
        >
          Next programs
        </Button>
      </Stack>
      {programs.ready && programItems.length === 0 ? (
        <Typography>No programs on this page.</Typography>
      ) : null}
      {tracks.isError ? (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={tracks.restart}>
              Restart class options
            </Button>
          }
        >
          Class options unavailable right now.
        </Alert>
      ) : null}
      <FormControl size="small" fullWidth disabled={disabled || !tracks.ready}>
        <InputLabel id="rate-class-option-label">Class option</InputLabel>
        <Select
          labelId="rate-class-option-label"
          label="Class option"
          value={
            savedTrack ||
            trackItems.some((track) => track.track_id === offeringId)
              ? offeringId
              : ""
          }
          onChange={(event) => onChange(event.target.value)}
        >
          <MenuItem value="">Choose a class option</MenuItem>
          {savedTrack &&
          !trackItems.some(
            (track) => track.track_id === savedTrack.track_id,
          ) ? (
            <MenuItem value={savedTrack.track_id}>
              {savedTrack.track_name}
            </MenuItem>
          ) : null}
          {trackItems.map((track) => (
            <MenuItem key={track.track_id} value={track.track_id}>
              {track.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button
          size="small"
          disabled={disabled || !tracks.ready || !tracks.canPrevious}
          onClick={tracks.previous}
        >
          Previous class options
        </Button>
        <Button
          size="small"
          disabled={disabled || !tracks.ready || !tracks.canNext}
          onClick={tracks.next}
        >
          Next class options
        </Button>
        <Button
          size="small"
          disabled={disabled || tracks.isLoading}
          onClick={tracks.restart}
        >
          Restart class options
        </Button>
      </Stack>
      {tracks.ready && trackItems.length === 0 ? (
        <Typography>
          {tracks.canNext
            ? "More class options remain"
            : "No class options on this page."}
        </Typography>
      ) : null}
    </Stack>
  );
}
